import { useEffect, useRef, useState } from "react";
import { Card, Col, Row, Form, Button } from "react-bootstrap";
import TextInput from "../../components/common/FormFields/TextInput.jsx";
import TextAreaInput from "../../components/common/FormFields/TextareaInput.jsx";
import { Formik } from "formik";
import * as Yup from "yup";
import { 
  getEmailTemplates, 
  addEmailTemplate, 
  updateEmailTemplate,
  getMailerMetas
} from "../../../services/MailerService.js";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from '../common/Toast/toast';
import { useAuth } from "../../../context/AuthContext";
import AsyncSelectDropdown from "../common/CustomAsyncDropdown.jsx";

function AddEmailTemplate() {
  const { id } = useParams();
  const [templateData, setTemplateData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [mailerMetas, setMailerMetas] = useState([]);
  const [loadingMailers, setLoadingMailers] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    // Check permissions for both add and edit
    const permission = id ? "email-template" : "email-template";
    const action = id ? "update" : "add";
    
    if (!hasPermission(permission, action, "lms")) {
      if (!hasShownToast.current) {
        toast.error(`You do not have permission to ${id ? 'edit' : 'add'} email templates.`);
        navigate("/email-templates");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate, id]);

  useEffect(() => {
    loadMailerMetas();
  }, []);

  useEffect(() => {
    if (id) {
      fetchTemplateDetails(id);
    } else {
      // Initialize with default values for new template
      setTemplateData({
        name: "",
        slug: "",
        mailermetaIds: "", // This will be a single ID string for submission
        subject: "",
        title: "",
        html: "",
        description: "",
        isDeleted: false,
        attachments: {}
      });
      setIsEditing(false);
    }
  }, [id]);

  const loadMailerMetas = async () => {
    try {
      setLoadingMailers(true);
      const response = await getMailerMetas(5000);
      
      // Handle API response structure
      let mailerData = [];
      if (response && response.data && Array.isArray(response.data)) {
        mailerData = response.data;
      } else if (response && Array.isArray(response)) {
        mailerData = response;
      }
      
      setMailerMetas(mailerData);
    } catch (error) {
      console.error("Error loading mailer metas:", error);
      toast.error("Error loading mailer configurations!");
      setMailerMetas([]);
    } finally {
      setLoadingMailers(false);
    }
  };

  const fetchTemplateDetails = async (templateId) => {
    try {
      setLoading(true);
      const response = await getEmailTemplates(500000);
      
      // Handle API response structure
      let templates = [];
      if (response && response.data && Array.isArray(response.data)) {
        templates = response.data;
      } else if (response && Array.isArray(response)) {
        templates = response;
      }
      
      const selectedTemplate = templates.find(
        (template) => template.id?.toString() === templateId
      );
      
      if (selectedTemplate) {
        // Extract mailer ID from the populated array for editing
        let mailermetaId = "";
        if (Array.isArray(selectedTemplate.mailermetaIds) && selectedTemplate.mailermetaIds.length > 0) {
          mailermetaId = selectedTemplate.mailermetaIds[0].id;
        } else if (typeof selectedTemplate.mailermetaIds === 'string') {
          mailermetaId = selectedTemplate.mailermetaIds;
        }
        
        // Format data for the form
        const formattedTemplate = {
          ...selectedTemplate,
          mailermetaIds: mailermetaId, // Convert to string ID for form
          attachments: selectedTemplate.attachments || {}
        };
        
        setTemplateData(formattedTemplate);
        setIsEditing(true);
      } else {
        toast.error("Email template not found!");
        navigate("/email-templates");
      }
    } catch (error) {
      console.error("Error fetching template details:", error);
      toast.error("Error loading email template!");
      navigate("/email-templates");
    } finally {
      setLoading(false);
    }
  };

  const getMailerOptions = () => {
    return mailerMetas.map((mailer) => ({
      value: mailer.id,
      label: mailer.name,
    }));
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, "Template name must be at least 3 characters")
      .max(100, "Template name must be less than 100 characters")
      .required("Template Name is required"),
    slug: Yup.string()
      .min(3, "Slug must be at least 3 characters")
      .max(50, "Slug must be less than 50 characters")
      .required("Slug is required")
      .matches(/^[a-z0-9-]+$/, "Slug should contain only lowercase letters, numbers and hyphens"),
    mailermetaIds: Yup.string().required("Mailer Configuration is required"),
    subject: Yup.string()
      .min(5, "Subject must be at least 5 characters")
      .max(200, "Subject must be less than 200 characters")
      .required("Subject is required"),
    title: Yup.string()
      .min(5, "Title must be at least 5 characters")
      .max(100, "Title must be less than 100 characters")
      .required("Title is required"),
    html: Yup.string()
      .min(50, "HTML content must be at least 50 characters")
      .required("HTML Content is required"),
    description: Yup.string()
      .min(10, "Description must be at least 10 characters")
      .max(500, "Description must be less than 500 characters")
      .required("Description is required"),
  });

  const getInitialValues = () => {
    if (templateData) {
      return {
        name: templateData.name || "",
        slug: templateData.slug || "",
        mailermetaIds: templateData.mailermetaIds || "",
        subject: templateData.subject || "",
        title: templateData.title || "",
        html: templateData.html || "",
        description: templateData.description || "",
        isDeleted: templateData.isDeleted || false,
        attachments: templateData.attachments || {}
      };
    }
    
    return {
      name: "",
      slug: "",
      mailermetaIds: "",
      subject: "",
      title: "",
      html: "",
      description: "",
      isDeleted: false,
      attachments: {}
    };
  };

  // Auto-generate slug from name
  const handleNameChange = (e, setFieldValue) => {
    const name = e.target.value;
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    setFieldValue('name', name);
    if (!isEditing) { // Only auto-generate slug for new templates
      setFieldValue('slug', slug);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      
      // Prepare payload - ensure mailermetaIds is sent as string
      const payload = {
        name: values.name.trim(),
        slug: values.slug.trim(),
        mailermetaIds: values.mailermetaIds, // Send as string ID
        subject: values.subject.trim(),
        title: values.title.trim(),
        html: values.html.trim(),
        description: values.description.trim(),
        isDeleted: false,
        attachments: values.attachments || {}
      };

      // console.log("Submission Payload:", payload);

      if (isEditing) {
        await updateEmailTemplate(payload, id);
        toast.success("Email template updated successfully!");
      } else {
        await addEmailTemplate(payload);
        toast.success("Email template created successfully!");
      }
      
      navigate("/email-templates");
    } catch (error) {
      console.error("Submission error:", error);
      
      // Handle different error types
      let errorMessage = `Failed to ${isEditing ? "update" : "create"} email template.`;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid data provided. Please check all fields.";
      } else if (error.response?.status === 401) {
        errorMessage = "You are not authorized to perform this action.";
      } else if (error.response?.status === 409) {
        errorMessage = "A template with this slug already exists.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading state
  if (loading || (!templateData && id)) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border"
                style={{ color: "#EA580C" }}
                role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading email template...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">
                  {isEditing ? "Edit Email Template" : "Add New Email Template"}
                </h4>
                <small className="text-muted">
                  {isEditing ? "Update the email template details" : "Create a new email template for system notifications"}
                </small>
              </div>
              <Link to="/email-templates" className="btn btn-secondary">
                <i className="fa fa-arrow-left me-2"></i>
                Back to List
              </Link>
            </Card.Header>
            
            <Card.Body>
              <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  isSubmitting,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      {/* Template Information Section */}
                      <Col md={12}>
                        <h5 className="mb-3"
                        style={{ color: "#EA580C" }}
                        >
                          <i className="fa fa-info-circle me-2"></i>
                          Template Information
                        </h5>
                      </Col>
                      
                      <Col md={6}>
                        <TextInput
                          label="Template Name"
                          name="name"
                          value={values.name}
                          onChange={(e) => handleNameChange(e, setFieldValue)}
                          onBlur={handleBlur}
                          error={errors.name}
                          touched={touched.name}
                          placeholder="e.g., Welcome New Student"
                          required
                        />
                      </Col>
                      
                      <Col md={6}>
                        <TextInput
                          label="Slug"
                          name="slug"
                          value={values.slug}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.slug}
                          touched={touched.slug}
                          placeholder="e.g., welcome-new-student"
                          required
                          helpText="Unique identifier (lowercase letters, numbers, hyphens only)"
                        />
                      </Col>

                      {/* Mailer Configuration */}
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-dark">
                            Mailer Configuration <span className="text-danger">*</span>
                          </Form.Label>
                          <AsyncSelectDropdown
                            value={getMailerOptions().find(option => option.value === values.mailermetaIds) || null}
                            onChange={(selectedOption) => setFieldValue("mailermetaIds", selectedOption?.value || "")}
                            options={getMailerOptions()}
                            placeholder="Select Mailer Configuration..."
                            isClearable
                            isLoading={loadingMailers}
                            isDisabled={loadingMailers || getMailerOptions().length === 0}
                          />
                          {errors.mailermetaIds && touched.mailermetaIds && (
                            <Form.Text className="text-danger">{errors.mailermetaIds}</Form.Text>
                          )}
                          {getMailerOptions().length === 0 && !loadingMailers && (
                            <Form.Text className="text-warning">
                              No mailer configurations available. Please create a mailer configuration first.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      {/* Email Content Section */}
                      <Col md={12}>
                        <h5 className="mb-3 mt-4"
                        style={{ color: "#EA580C" }}
                        >
                          <i className="fa fa-envelope me-2"></i>
                          Email Content
                        </h5>
                      </Col>

                      <Col md={12}>
                        <TextInput
                          label="Subject Line"
                          name="subject"
                          value={values.subject}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.subject}
                          touched={touched.subject}
                          placeholder="e.g., Welcome to ##company## 🎉 | Your Account Details"
                          required
                          helpText="Use ##variable## for dynamic content (e.g., ##company##, ##firstname##, ##email##)"
                        />
                      </Col>
                      
                      <Col md={12}>
                        <TextInput
                          label="Email Title"
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.title}
                          touched={touched.title}
                          placeholder="e.g., Welcome to SevenMentor"
                          required
                          helpText="Internal title for template identification"
                        />
                      </Col>

                      <Col md={12}>
                        <TextAreaInput
                          label="Description"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.description}
                          touched={touched.description}
                          placeholder="Describe when and how this template will be used..."
                          rows={3}
                          required
                          helpText="Template description for admin reference"
                        />
                      </Col>

                      {/* HTML Content Section */}
                      <Col md={12}>
                        <h5 className="mb-3 mt-4"
                        style={{ color: "#EA580C" }}
                        >
                          <i className="fa fa-code me-2"></i>
                          HTML Template
                        </h5>
                      </Col>

                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            HTML Content <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            name="html"
                            value={values.html}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            rows={20}
                            placeholder="<!DOCTYPE html>&#10;<html lang='en'>&#10;<head>&#10;  <meta charset='UTF-8'>&#10;  <meta name='viewport' content='width=device-width, initial-scale=1.0'>&#10;  <title>Email Template</title>&#10;</head>&#10;<body>&#10;  <h2>Hello ##firstname## ##lastname##!</h2>&#10;  <p>Welcome to ##company##</p>&#10;</body>&#10;</html>"
                            style={{ 
                              fontFamily: "Monaco, Menlo, 'Ubuntu Mono', monospace", 
                              fontSize: "13px",
                              lineHeight: "1.4"
                            }}
                            isInvalid={errors.html && touched.html}
                          />
                          {errors.html && touched.html && (
                            <Form.Text className="text-danger">{errors.html}</Form.Text>
                          )}
                          <Form.Text className="text-muted">
                            Complete HTML email template. Available variables: ##firstname##, ##lastname##, ##email##, ##company##, ##year##, etc.
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      {/* Preview Section */}
                      {values.html && (
                        <Col md={12}>
                          <h5 className="mb-3"
                          style={{ color: "#EA580C" }}
                          >
                            <i className="fa fa-eye me-2"></i>
                            Preview
                          </h5>
                          <div 
                            className="border rounded p-3 mb-3" 
                            style={{ 
                              backgroundColor: "#ffffff"
                            }}
                          >
                            <small className="text-muted d-block mb-2">HTML Preview:</small>
                            <iframe 
                              srcDoc={values.html}
                              title="HTML Preview"
                              style={{
                                width: "100%",
                                height: "300px",
                                border: "none",
                                backgroundColor: "#ffffff"
                              }}
                            />
                          </div>
                        </Col>
                      )}
                    </Row>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                      <Link to="/email-templates" className="btn btn-outline-secondary">
                        <i className="fa fa-times me-2"></i>
                        Cancel
                      </Link>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting || loadingMailers}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {isEditing ? "Updating..." : "Creating..."}
                          </>
                        ) : (
                          <>
                            <i className={`fa ${isEditing ? 'fa-save' : 'fa-plus'} me-2`}></i>
                            {isEditing ? "Update Template" : "Create Template"}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AddEmailTemplate;
